/**
 * Identita zařízení a session pro CDP tracking.
 *
 * Klient smí tvrdit jen „kdo je zařízení" – visitor id je anonymní klíč pro
 * agregaci, ne autorita. Server formát jen validuje (vis_/ses_ + 24 znaků);
 * kdo je člověk, řeší výhradně server přes přihlášení/checkout/newsletter.
 */

const VISITOR_KEY = 'zooshopik.cdp.visitor.v1';
const SESSION_KEY = 'zooshopik.cdp.session.v1';
const SESSION_CTX_KEY = 'zooshopik.cdp.sessionCtx.v1';

/** Po 30 minutách bez aktivity začíná nová session (GA-like pravidlo). */
const SESSION_IDLE_MS = 30 * 60 * 1000;

/** Kontext zachycený při vzniku session – UTM, referrer a vstupní stránka. */
export interface SessionContext {
  readonly utm: Record<string, string | null> | null;
  readonly referrer: string | null;
  readonly landingPath: string;
}

/**
 * 24 znaků [a-z0-9] z crypto.getRandomValues. Malá abeceda je podmnožinou
 * serverového formátu [A-Za-z0-9]{24}, takže validace projde vždy.
 * Exportováno i pro tracker (evt_ id mají stejný formát).
 */
export function randomId(prefix: string): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  let id = prefix;
  for (const byte of bytes) id += alphabet[byte % 36];
  return id;
}

// Privátní režim / zakázané úložiště: id žije jen v paměti na dobu stránky.
// Formát zůstává validní, jen se neudrží mezi načteními.
let memoryVisitorId: string | null = null;
let memorySession: { id: string; lastActivityAt: number } | null = null;
let memorySessionCtx: SessionContext | null = null;

/** Anonymní id prohlížeče. Vytvoří se napoprvé, pak se jen čte. */
export function getVisitorId(): string {
  try {
    const stored = window.localStorage.getItem(VISITOR_KEY);
    if (stored !== null && /^vis_[A-Za-z0-9]{24}$/.test(stored)) return stored;
    const fresh = randomId('vis_');
    window.localStorage.setItem(VISITOR_KEY, fresh);
    return fresh;
  } catch {
    if (memoryVisitorId === null) memoryVisitorId = randomId('vis_');
    return memoryVisitorId;
  }
}

interface StoredSession {
  readonly id: string;
  readonly lastActivityAt: number;
}

function readStoredSession(): StoredSession | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredSession).id === 'string' &&
      /^ses_[A-Za-z0-9]{24}$/.test((parsed as StoredSession).id) &&
      Number.isFinite((parsed as StoredSession).lastActivityAt)
    ) {
      return parsed as StoredSession;
    }
    return null;
  } catch {
    return memorySession;
  }
}

function writeStoredSession(session: StoredSession): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    memorySession = session;
  }
}

/**
 * Vrátí id aktuální session a posune čas poslední aktivity. Po 30 minutách
 * nečinnosti vznikne nové ses_ id a znovu se zachytí vstupní kontext
 * (kampaň, která návštěvu přivedla, se váže na session, ne na zařízení).
 */
export function getSessionId(): string {
  const now = Date.now();
  const current = readStoredSession();

  if (current !== null && now - current.lastActivityAt <= SESSION_IDLE_MS) {
    writeStoredSession({ id: current.id, lastActivityAt: now });
    return current.id;
  }

  const fresh = randomId('ses_');
  writeStoredSession({ id: fresh, lastActivityAt: now });
  captureSessionContext();
  return fresh;
}

/** UTM parametry z aktuální URL. Když žádný není, vrací null. */
function parseUtm(): Record<string, string | null> | null {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string | null> = {
    source: params.get('utm_source'),
    medium: params.get('utm_medium'),
    campaign: params.get('utm_campaign'),
    term: params.get('utm_term'),
    content: params.get('utm_content'),
  };
  const hasAny = Object.values(utm).some((value) => value !== null);
  return hasAny ? utm : null;
}

/** Referrer bereme jen cizí – vlastní navigace po webu není atribuce. */
function externalReferrer(): string | null {
  const referrer = document.referrer;
  if (referrer === '') return null;
  try {
    return new URL(referrer).origin === window.location.origin ? null : referrer;
  } catch {
    return null;
  }
}

/** Zachytí vstupní kontext (UTM/referrer/landing) do sessionStorage. */
export function captureSessionContext(): void {
  const context: SessionContext = {
    utm: parseUtm(),
    referrer: externalReferrer(),
    landingPath: window.location.pathname + window.location.search,
  };
  try {
    window.sessionStorage.setItem(SESSION_CTX_KEY, JSON.stringify(context));
  } catch {
    memorySessionCtx = context;
  }
}

/** Kontext session; když chybí (první volání), rovnou se zachytí. */
export function getSessionContext(): SessionContext {
  try {
    const raw = window.sessionStorage.getItem(SESSION_CTX_KEY);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) return parsed as SessionContext;
    }
  } catch {
    if (memorySessionCtx !== null) return memorySessionCtx;
  }
  captureSessionContext();
  try {
    const raw = window.sessionStorage.getItem(SESSION_CTX_KEY);
    if (raw !== null) return JSON.parse(raw) as SessionContext;
  } catch {
    // Spadne níž na paměťovou kopii.
  }
  return memorySessionCtx ?? { utm: null, referrer: null, landingPath: window.location.pathname };
}
